import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Bell, Check, CheckCheck, Inbox, UserPlus, FileText, Shield } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";

// =============================================================================
// NotificationsBell — T-151
//
// Top-right bell in AppShell. Wires to the existing `notifications` outbox
// filtered to the caller's own in_app rows. Migration 086 adds read_at +
// the two RPCs we hit here.
//
// UX:
//   - Bell + red dot + count when unread > 0
//   - Click bell → portal-rendered dropdown panel anchored under it
//   - Each row: icon (per event type) / subject / body preview / time-ago / unread dot
//   - Click a row → mark THAT row read + navigate to its source
//   - "Mark all read" button at the bottom of the panel
//
// Refresh strategy: TanStack Query polls every 60s while the user is active
// and refetches on focus. That's plenty for an internal tool — we're not
// trying to be Slack. When James eventually wants real-time, swap to a
// Supabase realtime subscription on the channel='in_app' filter.
// =============================================================================

interface NotificationRow {
  notification_id: number;
  event_type:      string;
  subject:         string | null;
  body_plain:      string | null;
  payload:         Record<string, any> | null;
  related_table:   string | null;
  related_id:      string | null;
  created_at:      string;
  read_at:         string | null;
}

const POLL_INTERVAL_MS = 60_000;
const PANEL_LIMIT = 30;

// Map event_type → an icon. Any unknown type falls back to <Inbox/>.
function iconForType(eventType: string) {
  if (eventType.startsWith("todo."))     return UserPlus;
  if (eventType.startsWith("licence."))  return Shield;
  if (eventType.startsWith("quote."))    return FileText;
  if (eventType.startsWith("bid."))      return FileText;
  if (eventType.startsWith("project."))  return FileText;
  return Inbox;
}

// Map event_type → a route the user should land on when they click.
// Returns null if no good destination is known — in that case we just
// mark read without navigating.
function routeForNotification(n: NotificationRow): string | null {
  const t = n.event_type;
  const p = n.payload ?? {};

  if (t === "todo.assigned" && p.project_id) {
    return `/projects/${p.project_id}`;
  }
  if (t.startsWith("bid.") && p.bid_id) {
    return `/bids/${p.bid_id}`;
  }
  if (t.startsWith("project.") && p.project_id) {
    return `/projects/${p.project_id}`;
  }
  if (t.startsWith("quote.") && p.quote_id) {
    return `/quotes/${p.quote_id}`;
  }
  if (t === "licence.verification_needed") {
    return `/admin/users`;
  }
  return null;
}

// Compact "12m ago" / "3h ago" / "2d ago" / fallback to date.
function timeAgo(iso: string): string {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)         return "just now";
  if (diff < 3600)       return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86_400) return `${Math.floor(diff / 86_400)}d ago`;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

// -----------------------------------------------------------------------------

export function NotificationsBell() {
  const user = useRequireAuth();
  if (!user) return null;

  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  // Fetch the recent in_app notifications for this user.
  // T-399 — recipient_user_id is users.user_id (what producers write). This
  // previously filtered on auth_user_id (a different uuid) so the bell never
  // showed targeted notifications. RLS re-pointed in mig 233.
  const { data: notifications = [], refetch } = useQuery({
    queryKey: ["notifications_in_app", user.user_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("notification_id, event_type, subject, body_plain, payload, related_table, related_id, created_at, read_at")
        .eq("channel", "in_app")
        .eq("recipient_user_id", user.user_id)
        .order("created_at", { ascending: false })
        .limit(PANEL_LIMIT);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const unreadCount = notifications.filter(n => !n.read_at).length;

  // Mark one
  const markOne = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase.schema("app").rpc("mark_notification_read", { p_id: id });
      if (error) throw error;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["notifications_in_app", user.user_id] });
      const prev = qc.getQueryData<NotificationRow[]>(["notifications_in_app", user.user_id]);
      qc.setQueryData<NotificationRow[]>(["notifications_in_app", user.user_id], (old) =>
        (old ?? []).map(n => n.notification_id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["notifications_in_app", user.user_id], ctx.prev);
    },
    onSettled: () => refetch(),
  });

  // Mark all
  const markAll = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema("app").rpc("mark_all_notifications_read");
      if (error) throw error;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["notifications_in_app", user.user_id] });
      const prev = qc.getQueryData<NotificationRow[]>(["notifications_in_app", user.user_id]);
      const now = new Date().toISOString();
      qc.setQueryData<NotificationRow[]>(["notifications_in_app", user.user_id], (old) =>
        (old ?? []).map(n => n.read_at ? n : { ...n, read_at: now })
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["notifications_in_app", user.user_id], ctx.prev);
    },
    onSettled: () => refetch(),
  });

  function openPanel() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    setOpen(true);
  }

  function handleClickRow(n: NotificationRow) {
    if (!n.read_at) markOne.mutate(n.notification_id);
    const route = routeForNotification(n);
    if (route) {
      setOpen(false);
      navigate(route);
    }
  }

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={open ? () => setOpen(false) : openPanel}
        className="p-2 rounded-md bg-ink-raised border border-line text-text relative hover:bg-ink-hover transition-colors"
        title={unreadCount > 0 ? `${unreadCount} unread` : "Notifications"}
        aria-label="Notifications"
      >
        <Bell size={14} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-lost text-white text-[9px] font-semibold flex items-center justify-center leading-none">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div
            className="fixed z-50 w-80 max-w-[calc(100vw-1rem)] bg-ink-card border border-line rounded-lg shadow-2xl overflow-hidden"
            style={{ top: pos.top, right: pos.right }}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-line">
              <div className="text-[12px] font-semibold text-text">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-1.5 text-text-muted font-normal">· {unreadCount} new</span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                  className="text-[10px] text-accent hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <CheckCheck size={11}/> Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {notifications.length === 0 && (
                <div className="px-4 py-8 text-center text-[11px] text-text-muted">
                  <Inbox size={20} className="mx-auto mb-2 opacity-50"/>
                  No notifications yet.
                </div>
              )}

              {notifications.map(n => {
                const Icon = iconForType(n.event_type);
                const unread = !n.read_at;
                const hasRoute = !!routeForNotification(n);
                return (
                  <button
                    key={n.notification_id}
                    onClick={() => handleClickRow(n)}
                    className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 border-b border-line/40 last:border-b-0 transition-colors ${
                      hasRoute ? "hover:bg-ink-raised cursor-pointer" : "cursor-default"
                    } ${unread ? "bg-accent/5" : ""}`}
                  >
                    <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      unread ? "bg-accent/15 text-accent" : "bg-ink-raised text-text-muted"
                    }`}>
                      <Icon size={13}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-[12px] leading-snug ${unread ? "font-medium text-text" : "text-text-muted"}`}>
                          {n.subject ?? n.event_type}
                        </div>
                        {unread && (
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" aria-label="unread"/>
                        )}
                      </div>
                      {n.body_plain && (
                        <div className="text-[11px] text-text-muted line-clamp-2 mt-0.5">
                          {n.body_plain}
                        </div>
                      )}
                      <div className="text-[10px] text-text-dim mt-0.5">
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {notifications.length > 0 && (
              <div className="px-3 py-1.5 border-t border-line text-[10px] text-text-dim text-center">
                Showing latest {Math.min(notifications.length, PANEL_LIMIT)} · click a notification to jump to it
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
